import { alpha } from '@mui/material/styles'
import React, { useEffect, useMemo } from 'react'
import { Box, Tab, Tabs, useMediaQuery, useTheme } from '@mui/material'
import Grid from '@mui/material/Grid'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import Loader from 'components/common/loader'
import { useActivateAccountMutation } from 'api/accounts/activateAccount'
import { isDeactivationPending, isReactivationInProgress } from 'settings/constants/status'
import { onChangeTab } from 'slices/customer/customerDetailsSlice'
import T from 'T'
import { get } from 'utils/lodash'
import { CUSTOMER_DETAILS_TABS_ID, CUSTOMER_DETAILS_TABS_ID_LIST } from 'containers/customer-details/settings'
import AccountActionsToolbar from 'components/customer-details/content/account-details/header/AccountActionsToolbar'
import { useLocation, useSearchParams } from 'react-router-dom-v5-compat'
import WithDeactivatedIndicator from 'components/customers/WithDeactivatedIndicator'
import { isLateFeeFeatureAvailable } from 'data/launch-darkly/featureSelectors'
import { useFlags } from 'launchdarkly-react-client-sdk'
import CustomerInfo from './CustomerInfo'

const { ACCOUNT_TAB_ID, SERVICES_TAB_ID, BILLING_TAB_ID, FEES_TAB_ID, ACTIVITY_TAB_ID } = CUSTOMER_DETAILS_TABS_ID

const Header = ({ accountId, isPageReady }) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const urlTab = (searchParams.get('tab') || '').toLowerCase()
  const customerDetails = useSelector(state => get(state, 'CustomersReducer.customerDetails', null))
  const [_, { isLoading: isActivateAccountLoading }] = useActivateAccountMutation()
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))
  const isCustomerActive = get(customerDetails, 'active', false)
  const isCustomerSuspended = get(customerDetails, 'isSuspended', false)
  const isDeactivationInFlight = isDeactivationPending(get(customerDetails, 'deactivationStatus'))
  const isCreatingWorkOrders = isReactivationInProgress(get(customerDetails, 'reactivationInProgressSince'))
  const flags = useFlags()
  const isLateFeeEnabled = isLateFeeFeatureAvailable({ flags })
  const isFeesTabEnabled = isLateFeeEnabled

  const bgcolor = useMemo(() => {
    if (isCustomerSuspended) {
      return theme.palette.background.suspension
    }
    if (!isCustomerActive) {
      return theme.palette.background.deactivated
    }
    // A run in flight is not yet a state of the account, so it tints the header rather than
    // claiming the deactivated treatment an account that is still serving customers hasn't earned.
    if (isDeactivationInFlight || isCreatingWorkOrders) {
      return alpha(theme.palette.warning.main, 0.08)
    }
    return theme.palette.background.default
  }, [isCustomerActive, isCustomerSuspended, isDeactivationInFlight, isCreatingWorkOrders, theme])

  const handleTabChange = (event, newValue) => {
    searchParams.set('tab', newValue)
    dispatch(onChangeTab(newValue))
    setSearchParams(searchParams, { replace: false, state: get(location, 'state') })
  }

  useEffect(() => {
    const urlTabId = (searchParams.get('tab') || '').toLowerCase()
    if (CUSTOMER_DETAILS_TABS_ID_LIST.includes(urlTabId)) {
      dispatch(onChangeTab(urlTabId))
    }
  }, [searchParams])

  useEffect(() => {
    if (urlTab === FEES_TAB_ID && isPageReady && !isFeesTabEnabled) {
      setTimeout(() => {
        handleTabChange(null, ACCOUNT_TAB_ID)
      }, 300)
    }
  }, [urlTab, isPageReady, isFeesTabEnabled, handleTabChange])

  return (
    <WithDeactivatedIndicator showIndicator={!isCustomerActive}>
      <Box
        position="relative"
        sx={{
          backgroundColor: bgcolor,
          // Slow enough to be seen as the header changing state rather than a repaint between two screens.
          transition: theme.transitions.create('background-color', {
            duration: theme.transitions.duration.complex,
            easing: theme.transitions.easing.easeInOut,
          }),
        }}
        width="100%"
      >
        {(isActivateAccountLoading || !isPageReady) && <Loader />}
        <Grid container direction="column" overflow="hidden">
          <Grid container direction={{ xs: 'column-reverse', lg: 'row' }} wrap="nowrap" size={12}>
            <Grid size="grow" sx={{ minWidth: 0 }}>
              <Grid container alignItems="center" justifyContent="space-between">
                <Grid size="grow">
                  <CustomerInfo />
                </Grid>
                {!isDesktop && (
                  <Grid>
                    <Box mr={3}>
                      <AccountActionsToolbar accountId={accountId} />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Grid>
            {isDesktop && (
              <Grid>
                <Box pt={1} pr="36px" whiteSpace="nowrap">
                  <AccountActionsToolbar accountId={accountId} />
                </Box>
              </Grid>
            )}
          </Grid>
          <Grid size={12}>
            <Box px={2} borderBottom={1} borderColor="divider">
              <Tabs value={urlTab} onChange={handleTabChange} sx={{ button: { textTransform: 'capitalize' } }}>
                <Tab value={ACCOUNT_TAB_ID} label={T.ACCOUNT} />
                <Tab value={SERVICES_TAB_ID} label={T.SERVICES} />
                <Tab value={BILLING_TAB_ID} label={T.BILLING} />
                {isFeesTabEnabled && <Tab value={FEES_TAB_ID} label={T.FEES} />}
                <Tab value={ACTIVITY_TAB_ID} label={T.ACTIVITY} />
              </Tabs>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </WithDeactivatedIndicator>
  )
}

Header.propTypes = {
  accountId: PropTypes.string.isRequired,
  isPageReady: PropTypes.bool.isRequired,
}

export default Header
